import {
  Box,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../../BaseHighchart';
import {
  ChartDataType,
} from '../../types';

import { DPV_NAME, CHART_TITLE_DPV } from '../../../../constants/graphs';
import apiClient from '../../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import { FetchError } from '../../../../views/types';
import ChartFilter from '../../ChartFilter';
import ConversationModal from '../../ConversationTab/conversationModal';
import {
  calculateYAxisLimits,
  getChartConfig,
  getStackedTooltip,
  getYAxis,
} from '../utils';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import DpvConfigContent from './DpvConfigContent';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

function DpvChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  height = 600,
  title = CHART_TITLE_DPV,
  yAxisTitle = 'DPV (kPa)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  isWeatherStation,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  isGeneratingPDF?: boolean;
  isWeatherStation?: boolean;
}) {
  const chartName = 'dpv';
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (interval) {
        const { data } = await apiClient.readings.getDpvData({
          farmId,
          sectorId,
          startDate: interval?.startDate || '',
          endDate: interval?.endDate || '',
          telemetryType: selectedSector?.telemetryType || 'wiseconn',
          sn: selectedSector?.telemetryType === 'sn' ? selectedSector.weatherZoneId : undefined,
          chart: DPV_NAME,
          isWeatherStation,
        });

        setChartData({
          series: data.dpvSeries,
          absoluteCategories: data.absoluteCategories,
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [farmId,
    interval,
    selectedSector,
    sectorId,
    isWeatherStation,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      return getStackedTooltip(this, conversations);
    },
  }), [conversations]);

  const chartConfig = useMemo(() => {
    if (!interval) return null;

    const yLimits = calculateYAxisLimits(chartData.series as any[]);
    const baseYAxis = getYAxis(yAxisTitle, hideYValues) as Highcharts.YAxisOptions;

    return getChartConfig(
      chartData,
      height,
      hideXValues,
      {
        ...baseYAxis,
        labels: {
          ...baseYAxis.labels,
          format: '{value:.2f}',
        },
        min: yLimits.min,
        max: yLimits.max,
      },
      dayjs(interval.startDate).valueOf(),
      dayjs(interval.endDate).valueOf(),
    );
  }, [
    hideXValues,
    chartData,
    hideYValues,
    yAxisTitle,
    height,
    interval?.startDate,
    interval?.endDate,
  ]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            DPV_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <div>
        <BaseHighchart
          chartView={chartView}
          sectorId={sectorId}
          graphName={DPV_NAME}
          filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
          title={title}
          yAxisTitle={yAxisTitle}
          series={chartData.series as Highcharts.SeriesOptionsType[]}
          tooltip={customTooltip}
          hideYValues={hideYValues}
          showFullScreen={true}
          legend={{ enabled: !hideLegend }}
          chartConfig={chartConfig}
          onConfigClick={handleConfigClick}
          onFullScreenClick={handleFullScreenClick}
          absoluteCategories={chartData.absoluteCategories}
          setModalState={setModalState}
          conversations={conversations}
          height={height}
          zoomSync={true}
          autoAdjustYAxis={true}
          interval={interval}
        />
        <ConversationModal
          modalState={modalState}
          setModalState={setModalState}
          farmId={farmId}
          sectorId={sectorId}
          chart={DPV_NAME}
          setConversationsFunction={setConversations}
          onConversationCreated={onConversationCreated}
        />
      </div>
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · DPV"
      >
        <DpvConfigContent sectorId={sectorId} />
      </ChartConfigDialog>
    </>
  );
}

export default DpvChart;
